import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { PanelLeft } from "lucide-react";
import { useMobile } from "../../hooks/useMobile";

function findEditorToolbarHost() {
  if (typeof document === "undefined") return null;
  const saveButton = Array.from(document.querySelectorAll("button")).find((button) => {
    const label = String(button.textContent || "").toUpperCase();
    return label.includes("REGISTRAR") && Boolean(button.querySelector(".lucide-save"));
  });
  return saveButton?.parentElement || null;
}

export default function EditorBinderLauncher() {
  const location = useLocation();
  const isMobile = useMobile();
  const [editorPresent, setEditorPresent] = useState(false);
  const [toolbarHost, setToolbarHost] = useState(null);

  const isEditorRoute =
    location.pathname === "/editor" || location.pathname.startsWith("/editor/");

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    let cancelled = false;
    let attempts = 0;
    let timer = null;

    const locateEditor = () => {
      if (cancelled) return;
      const canvas = document.querySelector("[data-article-canvas]");
      const host = findEditorToolbarHost();
      setEditorPresent(Boolean(canvas));
      setToolbarHost(host);

      // App-level siblings mount before the Editor route. Retry briefly so the
      // launcher can attach after the editor DOM exists.
      if ((!canvas || !host) && attempts < 12) {
        attempts += 1;
        timer = window.setTimeout(locateEditor, 80);
      }
    };

    locateEditor();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [location.pathname]);

  if ((!isEditorRoute && !editorPresent) || typeof document === "undefined") {
    return null;
  }

  const openBinder = () => {
    window.dispatchEvent(
      new CustomEvent("trufapp:toggle-binder", {
        detail: { source: "editor-file-manager" },
      }),
    );
  };

  const useToolbar = !isMobile && Boolean(toolbarHost);
  const button = useToolbar ? (
    <button
      type="button"
      onClick={openBinder}
      aria-label="Abrir administrador de archivos"
      title="Archivos"
      style={styles.toolbarButton}
    >
      <PanelLeft size={20} />
      <span>ARCHIVOS</span>
    </button>
  ) : (
    <button
      type="button"
      onClick={openBinder}
      aria-label="Abrir administrador de archivos"
      title="Abrir administrador de archivos"
      style={{ ...styles.fallbackButton, ...(isMobile ? styles.mobileFallback : styles.desktopFallback) }}
    >
      <PanelLeft size={21} />
      <span>Archivos</span>
    </button>
  );

  return createPortal(button, useToolbar ? toolbarHost : document.body);
}

const styles = {
  toolbarButton: {
    height: 44,
    minWidth: 44,
    padding: "0 11px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    borderRadius: 12,
    border: "1px solid rgba(212,175,55,0.28)",
    background: "rgba(212,175,55,0.10)",
    color: "#D4AF37",
    cursor: "pointer",
    touchAction: "manipulation",
    fontSize: "0.7rem",
    fontWeight: 800,
    letterSpacing: "0.06em",
    flexShrink: 0,
  },
  fallbackButton: {
    position: "fixed",
    zIndex: 10050,
    minWidth: 108,
    height: 50,
    padding: "0 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderRadius: 15,
    border: "1px solid rgba(212,175,55,0.34)",
    background: "rgba(18,16,14,0.96)",
    color: "#D4AF37",
    boxShadow: "0 10px 34px rgba(0,0,0,0.5)",
    backdropFilter: "blur(14px)",
    cursor: "pointer",
    touchAction: "manipulation",
    fontSize: "0.82rem",
    fontWeight: 800,
    letterSpacing: "0.01em",
  },
  mobileFallback: {
    right: "max(12px, env(safe-area-inset-right))",
    bottom: "calc(92px + env(safe-area-inset-bottom))",
  },
  desktopFallback: {
    left: "max(14px, env(safe-area-inset-left))",
    bottom: "max(18px, env(safe-area-inset-bottom))",
  },
};
